import { memo, useCallback, type NamedExoticComponent } from 'react';
import { Box, Flex, Text, useBoolean } from '@chakra-ui/react';
import { ChevronLeftIcon } from '@chakra-ui/icons';
import moment from 'moment';
import { useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';

import { Scenario } from 'models/response/scenario.interface';
import { getBlockDetails } from 'redux/PlanPageSlice';
import { getAllScenarios, getBaseScenario } from 'redux/ScenarioSlice';

import {
  PLAN_ACCENT_BLUE,
  PLAN_HEADER_HORIZONTAL_PADDING,
  PLAN_REVAMP_RADIUS_SM,
  PLAN_TEXT,
  accent,
  controlHeight,
  neutral,
  text as textToken,
} from '../planPageRevampUi';
import PlanEditedIcon from '../icons/PlanEditedIcon';

import PlanTimeRangeFilter from './PlanTimeRangeFilter';
import PlanScenarioComparisonPopover from './PlanScenarioComparisonPopover';
import PlanBuilderModeToggle from './PlanBuilderModeToggle';

interface Properties {
  refetchRequest: () => void;
}

/** PlanTimeRangeFilter drives the refetch through the store; nothing to relay. */
const noopRangeSelected = () => {};

const PlanPageTitleBar = ({ refetchRequest }: Properties) => {
  const navigate = useNavigate();
  const { modelId } = useParams();
  const blockDetail = useSelector(getBlockDetails);
  const scenarios = useSelector(getAllScenarios);
  const baseScenario = useSelector(getBaseScenario);
  const canCompare = (scenarios?.length ?? 0) > 1;
  const [isScenarioComparisonOpen, setScenarioComparisonOpen] = useBoolean();

  const persistScenario = useCallback(
    (scenario: Scenario) => {
      if (!blockDetail?.model_id) return;
      const planFilters = localStorage.getItem('global-modal-filters');
      const data = planFilters ? JSON.parse(planFilters) : {};
      const modalFilters = data[blockDetail.model_id] || {};
      modalFilters.scenario = scenario;
      localStorage.setItem(
        'global-modal-filters',
        JSON.stringify({ ...data, [blockDetail.model_id]: modalFilters }),
      );
    },
    [blockDetail?.model_id],
  );

  const editedLabel = blockDetail?.last_modified_at
    ? moment.utc(blockDetail.last_modified_at).fromNow(false)
    : null;

  const onBackToBlocks = useCallback(() => {
    navigate(`/model/${modelId}/blocks`);
  }, [modelId, navigate]);

  return (
    // py='12px' + the 32px controls below give this bar the same height as
    // DimensionsRevamp's header, so switching tabs no longer shifts the content
    // beneath by a few pixels.
    <Box w='100%' px={PLAN_HEADER_HORIZONTAL_PADDING} py='12px'>
      <Flex
        as='button'
        type='button'
        onClick={onBackToBlocks}
        alignItems='center'
        gap='2px'
        mb='6px'
        cursor='pointer'
        color={textToken.secondary}
        transition='color 0.12s ease'
        _hover={{ color: accent.solid }}
      >
        <ChevronLeftIcon boxSize={4} />
        <Text {...PLAN_TEXT.labelSmMedium}>Back to Blocks</Text>
      </Flex>

      <Flex w='100%' alignItems='center' justifyContent='space-between' gap={4}>
        {/* Title and "Edited …" sit on one line rather than stacked — the
            metadata reads as a caption right beside the name it describes,
            not as a second row competing with the KPI row below it. */}
        <Flex minW={0} alignItems='baseline' gap='10px'>
          <Text {...PLAN_TEXT.pageTitle} color={textToken.primary} noOfLines={1}>
            {blockDetail?.name ?? 'Block'}
          </Text>
          {/* Secondary text, not the accent blue the Figma frame uses. This line
              is passive metadata — nothing here is clickable — and accent-coloured
              copy directly under the page title pulled the eye away from both the
              title and the KPI row. Greyscale for structure is the same rule
              DimensionsRevamp follows. */}
          {editedLabel && (
            <Flex alignItems='center' gap='6px' flexShrink={0}>
              <PlanEditedIcon width={13} height={13} color={textToken.muted} />
              <Text {...PLAN_TEXT.metaAccent} color={textToken.secondary} whiteSpace='nowrap'>
                {`Edited ${editedLabel}`}
              </Text>
            </Flex>
          )}
        </Flex>

        <Flex alignItems='center' gap={3} flexShrink={0} flexWrap='wrap' justifyContent='flex-end'>
          {scenarios?.length > 0 && baseScenario ? (
            // Scenario + time range read as one control, exactly as the Dimensions
            // header pairs ScenarioForecast with TimeRangeFilter — same border,
            // radius and height, so the two modules' toolbars line up.
            <Flex
              alignItems='stretch'
              border='1px solid'
              borderColor={neutral.border}
              borderRadius={PLAN_REVAMP_RADIUS_SM}
              overflow='hidden'
              bg={neutral.surface}
              h={controlHeight}
              flexShrink={0}
            >
              {canCompare ? (
                <PlanScenarioComparisonPopover
                  isOpen={isScenarioComparisonOpen}
                  onOpen={setScenarioComparisonOpen.on}
                  onClose={setScenarioComparisonOpen.off}
                  refetchRequest={refetchRequest}
                  persistScenario={persistScenario}
                />
              ) : (
                <Flex alignItems='center' gap={2} px={3} h={controlHeight}>
                  <Box w='8px' h='8px' borderRadius='full' bg={PLAN_ACCENT_BLUE} flexShrink={0} />
                  <Text
                    {...PLAN_TEXT.toolbarTab}
                    color={textToken.primary}
                    noOfLines={1}
                    minW={'120px'}
                  >
                    {baseScenario.name}
                  </Text>
                </Flex>
              )}
              {/* No divider between the halves — DimensionsRevamp doesn't have
                one either. The date control carries its own #F5F0FF fill, and
                that edge against the white scenario half already reads as the
                seam; a hairline on top of it was one line too many. */}
              {/* The Dimensions header's own date control, copied across intact,
                rather than the plan page's separate TimeScaleFilter restyled to
                resemble it. `rangeSelected` is a no-op because the copy writes
                the plan slice's date itself and PlanPageContent refetches off
                that — see the note at the top of PlanTimeRangeFilter. */}
              <PlanTimeRangeFilter rangeSelected={noopRangeSelected} />
            </Flex>
          ) : null}

          <PlanBuilderModeToggle />
        </Flex>
      </Flex>
    </Box>
  );
};

export default memo(PlanPageTitleBar) as NamedExoticComponent<Properties>;
